import { Button, Checkbox, Classes, Intent, Menu, MenuItem, Spinner, Switch } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Popover2 } from "@blueprintjs/popover2";
import { format } from "date-fns";
import { ButtonContainer, IDataTableColumn, showError, ToolTipButton } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import { DashboardAbsencesVuDialog } from ".";
import { DashboardAbsenceDto, DashboardAbsenceIdsDto, DashboardAbsenceWalterSearch, DashboardApi } from "../../../api";
import { ERoutes } from "../../../AppRouter";
import { LinkButton, SearchTablePage, ViewButton } from "../../../components";
import { useAuth, useDialog, useEventsContext } from "../../../contexts";
import { useAbortableApiServiceFactory, useApiService, useTabMessage, useTheme, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { Guid } from "../../../utils/guid";

const StyledSwitch = styled(Switch)<{ color: string }>`
  & .${Classes.SWITCH} {
    &:hover {
      input:checked ~ .${Classes.CONTROL_INDICATOR} {
        background: ${props => props.color};
      }
    }
  }
`;

const PopoverContent = styled.div`
  padding: 0.5rem;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export interface IDashboardAbsencesWalterProps {}

export const DashboardAbsencesWalter: React.FunctionComponent<IDashboardAbsencesWalterProps> = props => {
  const { t } = useTl();
  const { user } = useAuth();
  const api = useApiService(DashboardApi);
  const apiFactory = useAbortableApiServiceFactory(DashboardApi);
  const lastAbortController = React.useRef<AbortController>();
  const [displayHidden, setDisplayHidden] = React.useState(false);
  const { theme } = useTheme();
  const [selectedIDs, setSelectedIDs] = React.useState<DashboardAbsenceIdsDto[]>([]);
  const [selectAllLoading, setSelectAllLoading] = React.useState(false);
  const [nbReturnedIds, setNbReturnedIds] = React.useState(0);
  const lastSearchObject = React.useRef<DashboardAbsenceWalterSearch>();

  const searchFunction = React.useCallback(
    (sObj?: DashboardAbsenceWalterSearch) => {
      sObj.hideMasquees = !displayHidden;
      if (JSON.stringify(sObj) !== JSON.stringify(lastSearchObject.current)) {
        lastSearchObject.current = sObj;
      }
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.dashboardSearchAbsencesWalter({ DashboardAbsenceWalterSearch: sObj });
    },
    [apiFactory, displayHidden]
  );
  const selectAll = React.useCallback(() => {
    setSelectAllLoading(true);
    api.dashboardSearchAbsencesWalterIds({ DashboardAbsenceWalterSearch: lastSearchObject.current }).then(allIds => {
      if (allIds.length === selectedIDs.length) {
        setSelectedIDs([]);
        setNbReturnedIds(0);
      } else {
        setSelectedIDs(allIds);
        setNbReturnedIds(allIds.length);
      }
      setSelectAllLoading(false);
    });
  }, [api, selectedIDs.length]);

  const toggleSelection = React.useCallback(
    (item: DashboardAbsenceDto) => {
      if (!selectedIDs.some(i => i.idapprenant === item.idapprenant && i.anneeScolaire === item.anneeScolaire)) {
        setSelectedIDs(ids => [...ids, { idapprenant: item.idapprenant, anneeScolaire: item.anneeScolaire }]);
      } else {
        setSelectedIDs(ids =>
          ids.filter(id => id.idapprenant !== item.idapprenant || id.anneeScolaire !== item.anneeScolaire)
        );
      }
    },
    [selectedIDs]
  );

  const isCrea = React.useMemo(() => user.hopeMainLevelName === "Conseiller CREA", [user.hopeMainLevelName]);

  const [rowLoading, setRowLoading] = React.useState<DashboardAbsenceDto>(null);

  const { showDialogPromise } = useDialog();
  const { dispatchEvent } = useEventsContext();

  const [currentSuiviData, setCurrentSuiviData] = React.useState<DashboardAbsenceDto>(null);
  const identifier = React.useMemo(() => Guid.newGuid(), []);

  const { openTabThenSendMessage } = useTabMessage(identifier);

  const marquerCommeVuOpenDialog = React.useCallback((row: DashboardAbsenceDto) => {
    setCurrentSuiviData(row);
  }, []);

  const marquerCommeVu = React.useCallback(
    async (dto: DashboardAbsenceDto) => {
      setCurrentSuiviData(null);
      if (!dto) {
        return;
      }

      openTabThenSendMessage(`#${ERoutes.apprenant}/${dto.idapprenant}/suiviApprenant/0/edit/walter`, dto);
    },
    [openTabThenSendMessage]
  );

  const masquer = React.useCallback(
    async (row: DashboardAbsenceDto) => {
      const res = await showDialogPromise({
        title: t(ETLCodes.MarquerAbsenceCommeVueConfirmTitle),
        message: t(ETLCodes.MarquerAbsenceCommeVueConfirmMessage)
      });
      if (res === "yes") {
        setRowLoading(row);
        await api.dashboardMasquerAbsence({ idapprenant: row.idapprenant, anneeScolaire: row.anneeScolaire });
        setRowLoading(null);
        dispatchEvent("SEARCH_TABLE_REFRESH");
        dispatchEvent("DASHBOARD_COUNTS_REFRESH_Absence");
      }
    },
    [api, dispatchEvent, showDialogPromise, t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "checkboxes",
        autoFitContent: true,
        header: () =>
          selectAllLoading ? (
            <Spinner size={20} />
          ) : (
            <Checkbox checked={nbReturnedIds > 0 && nbReturnedIds === selectedIDs.length} onChange={selectAll} />
          ),
        render: (item: DashboardAbsenceDto) => (
          <Checkbox
            checked={selectedIDs.some(
              i => i.idapprenant === item.idapprenant && i.anneeScolaire === item.anneeScolaire
            )}
            onChange={e => {
              toggleSelection(item);
            }}
          />
        )
      },
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: DashboardAbsenceDto) => (
          <ButtonContainer>
            {!isCrea && (
              <ToolTipButton
                minimal
                icon="tick"
                onClick={() => marquerCommeVuOpenDialog(row)}
                tooltipPosition="bottom"
                loading={
                  !!rowLoading &&
                  rowLoading.idapprenant === row.idapprenant &&
                  rowLoading.anneeScolaire === row.anneeScolaire
                }
                disabled={
                  !!rowLoading &&
                  (rowLoading.idapprenant !== row.idapprenant || rowLoading.anneeScolaire !== row.anneeScolaire)
                }
                tooltip={t(ETLCodes.AvertirConvoquer)}
              />
            )}
            {(!row.idsuserMasquage || !row.idsuserMasquage.includes(`|${user.iduser}|`)) && (
              <ToolTipButton
                tooltipPosition="bottom"
                tooltip={t(ETLCodes.MarquerCommeVu)}
                minimal
                icon="cross"
                onClick={() => masquer(row)}
                loading={
                  !!rowLoading &&
                  rowLoading.idapprenant === row.idapprenant &&
                  rowLoading.anneeScolaire === row.anneeScolaire
                }
                disabled={
                  !!rowLoading &&
                  (rowLoading.idapprenant !== row.idapprenant || rowLoading.anneeScolaire !== row.anneeScolaire)
                }
              />
            )}
            <ViewButton
              minimal={true}
              onClick={() => window.open(`#${ERoutes.apprenant}/${row.idapprenant}/Absence/view`, "_blank")}
            />
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.Idapprenant),
        fieldName: "idapprenant"
      },
      {
        header: () => t(ETLCodes.Apprenant),
        fieldName: "apprenant",
        render: (row: DashboardAbsenceDto) => (
          <LinkButton
            text={row.apprenant}
            onClick={() => window.open(`#${ERoutes.apprenant}/${row.idapprenant}/signaletique/view`)}
          />
        )
      },
      {
        header: () => t(ETLCodes.MetierStade),
        fieldName: "metierStade"
      },
      {
        header: () => t(ETLCodes.AbsencesInjustifiees),
        fieldName: "absencesInjustifiees"
      },
      {
        header: () => t(ETLCodes.DeltaDernierAvertissement),
        fieldName: "deltaDernierAvertissement"
      },
      {
        header: () => t(ETLCodes.DernierAvertissement),
        fieldName: "dernierAvertissement",
        render: (row: DashboardAbsenceDto) =>
          row.dernierAvertissement &&
          `${format(row.dernierAvertissement, "dd/MM/yyyy")} - ${row.typeDernierAvertissement}`
      },
      {
        header: () => t(ETLCodes.CodeClasse),
        fieldName: "codeClasse"
      }
    ],
    [
      isCrea,
      marquerCommeVuOpenDialog,
      masquer,
      nbReturnedIds,
      rowLoading,
      selectAll,
      selectAllLoading,
      selectedIDs,
      t,
      toggleSelection,
      user.iduser
    ]
  );

  const getCriteria = React.useCallback(async () => {
    return await api.dashboardGetAbsencesWalterCriterias();
  }, [api]);

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  const [actionLoading, setActionLoading] = React.useState(false);
  const maskSelected = React.useCallback(async () => {
    if (!selectedIDs?.length) {
      showError(t(ETLCodes.SelectAtLeastOne));
    }

    const res = await showDialogPromise({
      title: t(ETLCodes.MarquerAbsenceCommeVueConfirmTitle),
      message: t(ETLCodes.MarquerAbsenceCommeVueConfirmMessage)
    });
    if (res === "yes") {
      setActionLoading(true);
      await api.dashboardMasquerAbsenceMultiple({ DashboardAbsenceIdsDto: selectedIDs });
      setActionLoading(false);
      dispatchEvent("SEARCH_TABLE_REFRESH");
      dispatchEvent("DASHBOARD_COUNTS_REFRESH_Absence");
    }
  }, [api, dispatchEvent, selectedIDs, showDialogPromise, t]);

  const buttons = React.useMemo(
    () => (
      <ButtonsContainer>
        <Popover2
          position="bottom"
          content={
            <PopoverContent>
              <Menu>
                <MenuItem
                  icon={IconNames.EYE_OFF}
                  text={t(ETLCodes.MasquerSelection)}
                  intent={Intent.PRIMARY}
                  onClick={() => maskSelected()}
                />
              </Menu>
            </PopoverContent>
          }
        >
          <Button
            icon={IconNames.CHEVRON_DOWN}
            text={t(ETLCodes.GlobalActions)}
            minimal={true}
            intent={Intent.PRIMARY}
            loading={actionLoading}
          ></Button>
        </Popover2>
        <StyledSwitch
          color={theme.primaryColor}
          label={t(ETLCodes.AfficherMasquees)}
          checked={displayHidden}
          onChange={e => {
            setDisplayHidden(e.currentTarget.checked);
          }}
        ></StyledSwitch>
      </ButtonsContainer>
    ),
    [actionLoading, displayHidden, maskSelected, t, theme.primaryColor]
  );

  return (
    <>
      <SearchTablePage
        searchStateInitialSearch={false}
        withCard={false}
        columns={columns}
        getCriteriasFunction={getCriteria}
        searchFunction={searchFunction}
        onAbort={onAbort}
        sortKeys={{ apprenant: "ASC" }}
        rightElement={buttons}
      />
      {currentSuiviData && (
        <DashboardAbsencesVuDialog
          currentRow={currentSuiviData}
          dialogOpen={!!currentSuiviData}
          onClose={marquerCommeVu}
        />
      )}
    </>
  );
};
